import isValidABN from "is-valid-abn";
import isValidACN from "is-valid-acn";

/**
 * Business registration identifiers, keyed by **type** rather than by country.
 *
 * A CRN validates the same way whoever asks for it, so the rules live here while the API says which
 * types a country accepts. That split is what stops the old failure mode: registration options were
 * country-keyed in four separate places, and every one of them had to be remembered when a country
 * was added — which is why a UK operator was being asked for an ABN.
 *
 * Validation is not authoritative — the backend re-checks. An unknown type therefore passes rather
 * than blocking the user, so the backend can start accepting a new type without a frontend release.
 */
export type RegistrationTypeSpec = {
  label: string;
  placeholder: string;
  inputType: "text" | "number";
  validate: (value: string) => boolean;
  errorMessage: string;
};

const REGISTRATION_TYPES: Record<string, RegistrationTypeSpec> = {
  ABN: {
    label: "ABN",
    placeholder: "12345678901",
    // Numeric, but not `number`: a leading zero and the spaced format both matter.
    inputType: "text",
    // A weighted modulus check, not a pattern — this is why the rule cannot be served as data.
    validate: (value) => isValidABN(value),
    errorMessage: "Field must be a valid ABN.",
  },
  ACN: {
    label: "ACN",
    placeholder: "123456789",
    inputType: "text",
    validate: (value) => isValidACN(value),
    errorMessage: "Field must be a valid ACN.",
  },
  EIN: {
    label: "EIN",
    placeholder: "12-3456789",
    inputType: "text",
    validate: (value) => /^\d{2}-?\d{7}$/.test(value),
    errorMessage: "Field must be a valid EIN (format: XX-XXXXXXX).",
  },
  NZBN: {
    label: "NZBN",
    placeholder: "9429012345678",
    inputType: "text",
    validate: (value) => /^\d{13}$/.test(value),
    errorMessage: "Field must be a valid NZBN (13 digits).",
  },
  /** Companies House number: eight digits, or a two-letter prefix and six digits (SC/NI/OC…). */
  CRN: {
    label: "Company Number",
    placeholder: "12345678",
    inputType: "text",
    validate: (value) => /^(\d{8}|[A-Za-z]{2}\d{6})$/.test(value.replace(/\s/g, "")),
    errorMessage: "Field must be a valid company number (8 digits, or 2 letters and 6 digits).",
  },
  /** Unique Taxpayer Reference. */
  UTR: {
    label: "UTR",
    placeholder: "1234567890",
    inputType: "text",
    validate: (value) => /^\d{10}$/.test(value.replace(/\s/g, "")),
    errorMessage: "Field must be a valid UTR (10 digits).",
  },
  /**
   * Swiss UID: "CHE" and nine digits, conventionally dotted in threes. The VAT variant appends
   * " MWST" / " TVA" / " IVA"; it is deliberately rejected — the backend's registration-id field is
   * 16 characters and the bare UID is what the register holds.
   */
  UID: {
    label: "UID",
    placeholder: "CHE-123.456.789",
    inputType: "text",
    validate: (value) => /^CHE-?\d{3}\.?\d{3}\.?\d{3}$/i.test(value.replace(/\s/g, "")),
    errorMessage: "Field must be a valid UID (CHE-123.456.789).",
  },
  /** GB VAT number: nine digits, or twelve for a branch trader, with an optional GB prefix. */
  VAT: {
    label: "VAT Number",
    placeholder: "GB123456789",
    inputType: "text",
    validate: (value) => /^(GB)?(\d{9}|\d{12})$/.test(value.replace(/\s/g, "").toUpperCase()),
    errorMessage: "Field must be a valid VAT number (9 or 12 digits).",
  },
};

export const getRegistrationTypeSpec = (type?: string): RegistrationTypeSpec | undefined =>
  type ? REGISTRATION_TYPES[type.toUpperCase()] : undefined;

/**
 * Validates an identifier against its own type's rule.
 *
 * @returns `{ valid: true }` for an unknown or absent type — deferring to the backend rather than
 * rejecting something it may well accept.
 */
export const validateRegistrationId = (type: string | undefined, value: string): { valid: boolean; message?: string } => {
  const spec = getRegistrationTypeSpec(type);

  if (!spec) return { valid: true };
  if (spec.validate(value)) return { valid: true };

  return { valid: false, message: spec.errorMessage };
};

/** Dropdown options for the types a country accepts, labelled from the registry. */
export const getRegistrationTypeOptions = (types: string[]): { name: string; value: string }[] =>
  types.map((type) => ({ name: getRegistrationTypeSpec(type)?.label ?? type, value: type }));
